/**
 * ProposalCard — THE text-edit proposal surface for the chat thread.
 *
 * One presentation for every "Asthra proposes replacement text →
 * writer approves or rejects" moment, regardless of who initiated it:
 *   - EditProposalMessage (writer-initiated highlight edit) adapts
 *     the /api/selection-edit proposal onto this card.
 *   - WriteApprovalMessage (agent-initiated doc_write_section gate)
 *     adapts the WebSocket write_approval request onto this card.
 *
 * The card renders as an assistant-style bubble: label row (Asthra ·
 * time + intent-style chip), a mono header naming the target, the
 * proposed content as MARKDOWN (tables/headings preview the way they
 * land in Word), a collapsed "show original text" disclosure, an
 * optional warning line, an optional extra body slot (e.g. the
 * write-approval edit-before-approving textarea), and Approve/Reject
 * buttons directly below the output.
 *
 * A text change also opens in the review pop-up (``ReviewPopup``, the same
 * view as the review window: changes / before and after / new text, over the
 * pane with Expand). It opens when the card arrives; the card keeps a short
 * line with "Review change" to reopen it and the Approve / Reject buttons.
 * A custom preview (``previewNode``, e.g. a table edit) stays in the card.
 *
 * SRP: pure presentation. Callers own all state machines and apply
 * logic; the card never talks to Word, the bridge, or the backend.
 */

import { makeStyles } from "@fluentui/react-components";
import type React from "react";
import { useMemo, useState } from "react";
import { MarkdownWithTables } from "./MarkdownWithTables";
import remarkGfm from "remark-gfm";
import { repairPartialTables } from "../../services/partialTables";
import { remarkSubSup } from "../../services/remarkSubSup";
import { brandColors, fonts, inkColors, spacing, statusColors } from "../tokens";
import AsthraBubbleSurface from "./AsthraBubbleSurface";
import DiffView from "./DiffView";
import { formatTime } from "./MessageBubble";
import { assistantMarkdownRules } from "./markdownStyles";
import type { ReviewItem } from "../review/reviewProtocol";
import type { TableEditSnippet } from "../services/tableEdit";
import { ReviewPopup } from "../review/ReviewPopup";

export interface ProposalCardProps {
  /** Chip text next to "Asthra · HH:MM" (e.g. "Edit highlight",
   *  "Write approval"). */
  chipText: string;
  /** One-line mono header naming the target + instruction/reason. */
  header: string;
  /** Proposed content, rendered as markdown. Ignored when ``previewNode``
   *  is provided. */
  previewMarkdown: string;
  /** Custom preview element rendered in place of the markdown block —
   *  e.g. the table-edit snippet diff with changed cells highlighted.
   *  When set, ``previewMarkdown`` is not rendered. */
  previewNode?: React.ReactNode;
  /** The before-text shown behind the "show original text" toggle.
   *  Omit to hide the disclosure entirely. */
  originalText?: string | null;
  /** When set, the preview leads with a word diff of this before-text
   *  against ``diffAfter`` (or ``previewMarkdown``), with a toggle to the
   *  clean markdown preview. Replaces the "show original text" disclosure
   *  (the diff already shows the original). Ignored with ``previewNode``. */
  diffBefore?: string | null;
  /** The after-text for the diff; defaults to ``previewMarkdown``. */
  diffAfter?: string;
  /** Optional warning line (ambiguity / drift / disconnect). */
  warning?: string | null;
  /** Optional slot rendered between the preview and the actions —
   *  e.g. the write-approval edit-before-approving textarea. */
  extraBody?: React.ReactNode;
  /** Optional meta chips row under the header (e.g. §9.2 · Insert). */
  metaChips?: readonly string[];
  /** True while the decision is being applied — disables both
   *  buttons and swaps the approve label for ``busyLabel``. */
  busy?: boolean;
  /** True when the card can't currently be decided (e.g. bridge
   *  disconnected) — disables both buttons without the busy label. */
  disabled?: boolean;
  approveLabel?: string;
  busyLabel?: string;
  ariaLabel: string;
  /** Called with the writer's edited text when they approve an edit made via
   *  ``editableText``; with no argument for a plain Approve. */
  onApprove: (editedText?: string) => void;
  onReject: () => void;
  /** When provided, an "Edit text" action opens this text in a textarea so the
   *  writer can change it themselves before approving. Omit to hide it. */
  editableText?: string;
  /** When provided, a "Revise" action appears alongside Approve/Reject.
   *  Choosing it opens an inline input; submitting calls ``onRevise`` with
   *  the writer's revision instruction (the caller re-runs the rewrite
   *  against the current proposal + this instruction). Omit to hide the
   *  affordance entirely (write-approval / ad-hoc cards stay Approve/Reject). */
  onRevise?: (revisionInstruction: string) => void;
  /** Optional status line shown in place of the actions while ``busy`` — e.g.
   *  the live "Revising…" progress while a revision re-run is in flight. */
  statusLine?: string | null;
  /** A table edit: reviewed in the pop-up as changed cells (``previewNode``
   *  stays the card's own preview). */
  tableSnippet?: TableEditSnippet | null;
}

const useStyles = makeStyles({
  outer: {
    alignSelf: "flex-start",
    maxWidth: "95%",
  },
  bubble: {
    padding: spacing.bubblePadding,
    fontSize: "12.5px",
    wordBreak: "break-word",
  },
  intentChip: {
    fontSize: "8.5px",
    fontFamily: fonts.mono,
    fontWeight: 600,
    letterSpacing: "0.3px",
    textTransform: "uppercase",
    color: brandColors.mid,
    backgroundColor: brandColors.light,
    border: `1px solid ${brandColors.glow}`,
    borderRadius: "8px",
    padding: "0px 6px",
    lineHeight: "13px",
    whiteSpace: "nowrap",
    marginLeft: "5px",
  },
  header: {
    fontFamily: fonts.mono,
    fontSize: "10.5px",
    color: inkColors.ink3,
    marginBottom: "6px",
    letterSpacing: "0.2px",
  },
  metaRow: {
    display: "flex",
    columnGap: "6px",
    rowGap: "4px",
    flexWrap: "wrap",
    marginBottom: "6px",
  },
  metaChip: {
    fontSize: "10px",
    fontFamily: fonts.mono,
    fontWeight: 600,
    color: inkColors.ink2,
    backgroundColor: brandColors.light,
    border: `1px solid ${brandColors.glow}`,
    borderRadius: "999px",
    padding: "1px 8px",
    whiteSpace: "nowrap",
  },
  markdown: {
    ...assistantMarkdownRules,
  },
  viewToggle: {
    display: "flex",
    justifyContent: "flex-end",
    marginBottom: "4px",
  },
  originalToggle: {
    marginTop: "6px",
    background: "none",
    border: "none",
    padding: 0,
    fontSize: "10px",
    fontFamily: fonts.mono,
    color: inkColors.ink4,
    cursor: "pointer",
    textDecoration: "underline",
  },
  originalPane: {
    marginTop: "4px",
    fontSize: "11.5px",
    lineHeight: 1.45,
    color: inkColors.ink2,
    whiteSpace: "pre-wrap",
    maxHeight: "140px",
    overflowY: "auto",
    padding: "6px 8px",
    borderRadius: "4px",
    backgroundColor: "var(--danger-soft)",
    border: "1px solid color-mix(in srgb, var(--danger) 35%, transparent)",
  },
  warning: {
    marginTop: "6px",
    fontSize: "11px",
    color: statusColors.warn,
  },
  actions: {
    display: "flex",
    columnGap: "8px",
    justifyContent: "flex-end",
    marginTop: "8px",
  },
  statusLine: {
    marginTop: "8px",
    fontSize: "11px",
    fontFamily: fonts.mono,
    color: inkColors.ink3,
    fontStyle: "italic",
  },
  revisePanel: {
    marginTop: "8px",
    display: "flex",
    flexDirection: "column",
    rowGap: "6px",
  },
  reviseTextarea: {
    width: "100%",
    boxSizing: "border-box",
    minHeight: "52px",
    resize: "vertical",
    fontFamily: fonts.body,
    fontSize: "12px",
    lineHeight: 1.45,
    color: inkColors.ink2,
    padding: "6px 8px",
    borderRadius: "4px",
    border: `1px solid ${brandColors.glow}`,
    ":focus": { outline: "none", border: `1px solid ${brandColors.mid}` },
  },
  reviseActions: {
    display: "flex",
    columnGap: "8px",
    justifyContent: "flex-end",
  },
  reviseBtn: {
    borderRadius: "8px",
    padding: "6px 14px",
    fontSize: "12px",
    fontWeight: 600,
    cursor: "pointer",
    backgroundColor: "transparent",
    border: `1px solid ${brandColors.mid}`,
    color: brandColors.primary,
    marginRight: "auto",
    ":disabled": { opacity: 0.6, cursor: "default" },
  },
  reviseSendBtn: {
    border: "none",
    borderRadius: "8px",
    padding: "6px 14px",
    fontSize: "12px",
    fontWeight: 600,
    cursor: "pointer",
    backgroundColor: brandColors.primary,
    color: "var(--on-brand)",
    ":disabled": { opacity: 0.6, cursor: "default" },
  },
  approveBtn: {
    border: "none",
    borderRadius: "8px",
    padding: "6px 14px",
    fontSize: "12px",
    fontWeight: 600,
    cursor: "pointer",
    backgroundColor: brandColors.primary,
    color: "var(--on-brand)",
    ":disabled": { opacity: 0.6, cursor: "default" },
  },
  rejectBtn: {
    borderRadius: "8px",
    padding: "6px 14px",
    fontSize: "12px",
    fontWeight: 600,
    cursor: "pointer",
    backgroundColor: "transparent",
    border: `1px solid ${inkColors.ink4}`,
    color: inkColors.ink2,
    ":disabled": { opacity: 0.6, cursor: "default" },
  },
});

export const ProposalCard: React.FC<ProposalCardProps> = ({
  chipText,
  header,
  previewMarkdown,
  previewNode = null,
  originalText = null,
  diffBefore = null,
  diffAfter,
  warning = null,
  extraBody = null,
  metaChips,
  busy = false,
  disabled = false,
  approveLabel = "Approve",
  busyLabel = "Applying…",
  ariaLabel,
  onApprove,
  onReject,
  onRevise,
  editableText,
  statusLine = null,
  tableSnippet = null,
}) => {
  const styles = useStyles();
  const [showOriginal, setShowOriginal] = useState(false);
  // Diff-first: the word diff is the default view when a before-text exists.
  const [showDiff, setShowDiff] = useState(true);
  const hasDiff = !previewNode && !!diffBefore;
  // Inline revise input: open reveals a textarea in place of the actions.
  const [reviseOpen, setReviseOpen] = useState(false);
  const [reviseText, setReviseText] = useState("");
  // Inline edit: the writer's own wording, approved as-is.
  const [editOpen, setEditOpen] = useState(false);
  const [editText, setEditText] = useState("");
  const time = formatTime(Date.now());
  const blocked = busy || disabled;
  // Text and table changes are reviewed in the pop-up, opened as the card
  // arrives. Any other custom preview stays in the card.
  const popupable = !previewNode || Boolean(tableSnippet);
  const [popupOpen, setPopupOpen] = useState(popupable);
  const reviewItem = useMemo<ReviewItem>(
    () => ({
      id: ariaLabel,
      sectionNumber: "",
      sectionTitle: header,
      kind: tableSnippet ? "table" : "text",
      table: tableSnippet,
      before: diffBefore ?? originalText ?? "",
      after: diffAfter ?? previewMarkdown,
      fills: [],
      stillOpen: [],
      sourceNote: warning,
      revisionCount: 0,
    }),
    [ariaLabel, header, tableSnippet, diffBefore, originalText, diffAfter, previewMarkdown, warning],
  );

  const submitRevision = () => {
    const text = reviseText.trim();
    if (!text || !onRevise) return;
    onRevise(text);
    // Caller flips the card to busy/"revising"; reset the local input so a
    // later revise starts clean.
    setReviseText("");
    setReviseOpen(false);
  };

  const label = (
    <>
      {`Review agent · ${time}`}
      <span className={styles.intentChip} aria-label={`Intent: ${chipText}`}>
        {chipText}
      </span>
    </>
  );

  return (
    <AsthraBubbleSurface
      label={label}
      className={styles.outer}
      // biome-ignore lint/a11y/useSemanticElements: AsthraBubbleSurface forwards role to its inner div
      bubbleClassName={styles.bubble}
      role="region"
      ariaLabel={ariaLabel}
    >
      <div className={styles.header}>{header}</div>
      {metaChips && metaChips.length > 0 ? (
        <div className={styles.metaRow}>
          {metaChips.map((chip) => (
            <span key={chip} className={styles.metaChip}>
              {chip}
            </span>
          ))}
        </div>
      ) : null}
      {popupable && popupOpen ? (
        <ReviewPopup
          item={reviewItem}
          phase={busy ? (statusLine ? "revising" : "applying") : "reviewing"}
          progressLabel={statusLine}
          error={disabled ? (warning ?? "This change can't be decided right now.") : null}
          onApprove={(content) => {
            if (!blocked) onApprove(content);
          }}
          canEdit={editableText !== undefined && reviewItem.kind === "text"}
          onReject={() => {
            if (!blocked) onReject();
          }}
          onRevise={onRevise}
          onClose={() => setPopupOpen(false)}
        />
      ) : null}
      {popupable ? (
        <div className={styles.viewToggle}>
          <button
            type="button"
            className={styles.originalToggle}
            style={{ marginTop: 0 }}
            onClick={() => setPopupOpen(true)}
            disabled={popupOpen}
          >
            {popupOpen ? "Open for review" : "Review change"}
          </button>
        </div>
      ) : null}
      {popupable ? null : hasDiff ? (
        <div className={styles.viewToggle}>
          <button
            type="button"
            className={styles.originalToggle}
            style={{ marginTop: 0 }}
            onClick={() => setShowDiff((s) => !s)}
            aria-pressed={!showDiff}
          >
            {showDiff ? "show clean preview" : "show changes"}
          </button>
        </div>
      ) : null}
      {popupable ? null : previewNode ? (
        previewNode
      ) : hasDiff && showDiff ? (
        <DiffView before={diffBefore ?? ""} after={diffAfter ?? previewMarkdown} maxHeight={320} />
      ) : (
        <div className={styles.markdown}>
          <MarkdownWithTables remarkPlugins={[[remarkGfm, { singleTilde: false }], remarkSubSup]}>
            {repairPartialTables(previewMarkdown)}
          </MarkdownWithTables>
        </div>
      )}
      {originalText && !hasDiff && !popupable ? (
        <>
          <button
            type="button"
            className={styles.originalToggle}
            onClick={() => setShowOriginal((s) => !s)}
            aria-expanded={showOriginal}
          >
            {showOriginal ? "hide original text" : "show original text"}
          </button>
          {showOriginal && <div className={styles.originalPane}>{originalText}</div>}
        </>
      ) : null}
      {extraBody}
      {warning ? <div className={styles.warning}>{warning}</div> : null}
      {busy && statusLine ? (
        <div className={styles.statusLine} aria-live="polite">
          {statusLine}
        </div>
      ) : onRevise && reviseOpen && !blocked ? (
        <div className={styles.revisePanel}>
          <textarea
            className={styles.reviseTextarea}
            value={reviseText}
            onChange={(e) => setReviseText(e.target.value)}
            placeholder="What should change? (e.g. make it more concise, cite the source, keep the number)"
            aria-label="Revision instruction"
            // Cmd/Ctrl+Enter submits; plain Enter allows multi-line.
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                submitRevision();
              }
            }}
          />
          <div className={styles.reviseActions}>
            <button
              type="button"
              className={styles.rejectBtn}
              onClick={() => {
                setReviseOpen(false);
                setReviseText("");
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className={styles.reviseSendBtn}
              onClick={submitRevision}
              disabled={!reviseText.trim()}
            >
              Send revision
            </button>
          </div>
        </div>
      ) : editableText !== undefined && editOpen && !blocked ? (
        <div className={styles.revisePanel}>
          <textarea
            className={styles.reviseTextarea}
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            aria-label="Edit the proposed text"
            rows={Math.min(14, Math.max(4, editText.split("\n").length + 1))}
          />
          <div className={styles.reviseActions}>
            <button type="button" className={styles.rejectBtn} onClick={() => setEditOpen(false)}>
              Cancel
            </button>
            <button
              type="button"
              className={styles.approveBtn}
              onClick={() => {
                setEditOpen(false);
                onApprove(editText);
              }}
              disabled={!editText.trim()}
            >
              Approve edit
            </button>
          </div>
        </div>
      ) : (
        <div className={styles.actions}>
          {editableText !== undefined ? (
            <button
              type="button"
              className={styles.reviseBtn}
              onClick={() => {
                setEditText(editableText);
                setEditOpen(true);
              }}
              disabled={blocked}
            >
              Edit text
            </button>
          ) : null}
          {onRevise ? (
            <button type="button" className={styles.reviseBtn} onClick={() => setReviseOpen(true)} disabled={blocked}>
              Revise
            </button>
          ) : null}
          <button type="button" className={styles.rejectBtn} onClick={onReject} disabled={blocked}>
            Reject
          </button>
          <button type="button" className={styles.approveBtn} onClick={() => onApprove()} disabled={blocked}>
            {busy ? busyLabel : approveLabel}
          </button>
        </div>
      )}
    </AsthraBubbleSurface>
  );
};

export default ProposalCard;
